import { collectOptionPrimaryComponentBlocks } from "@shared/dtoolsOptionComponentGroups";
import { inferDtoolsEquipmentRole } from "@shared/dtoolsEquipmentRoles";
import { collectServiceLines } from "@shared/dtoolsReviewNavigation";
import type { DtoolsReviewSystem } from "@shared/dtoolsStagingTransform";

function systemHaystack(system: DtoolsReviewSystem): string {
  const blocks = collectOptionPrimaryComponentBlocks(system);
  return blocks
    .map((block) =>
      [
        block.line.category,
        block.line.subcategory,
        block.line.manufacturer,
        block.line.model,
        block.line.polishedDescription,
        block.line.dtoolsDescription,
        block.line.cleoDescription,
      ]
        .map((value) => String(value ?? "").trim())
        .filter(Boolean)
        .join(" "),
    )
    .join(" ");
}

function hasServiceLines(system: DtoolsReviewSystem): boolean {
  const roomServices = system.rooms.some((room) => collectServiceLines(room.primaries).length > 0);
  const systemServices = collectServiceLines(system.systemLevelPrimaries).length > 0;
  return roomServices || systemServices;
}

/** One-line explanation of what the system investment covers — PDF only. */
export function resolvePdfSystemValueStory(system: DtoolsReviewSystem): string {
  const haystack = systemHaystack(system);
  const blocks = collectOptionPrimaryComponentBlocks(system);
  const roles = blocks.map((block) => inferDtoolsEquipmentRole(block.line));
  const includesServices = hasServiceLines(system);
  const serviceSuffix = includesServices
    ? ", including professional installation, testing, and commissioning"
    : "";

  if (/\b(lutron|homeworks|radio ra|ketra|lighting control|lighting system)\b/i.test(haystack)) {
    return `This represents the complete lighting control scope for the home, including processors, keypads, programming, and reliable day-to-day operation${serviceSuffix}.`;
  }

  const isCinema =
    roles.some((role) => ["Projection", "Video Processing", "Display", "Screen"].includes(role)) &&
    roles.some((role) => ["Audio Processing", "Amplification", "Front Speakers", "Subwoofers"].includes(role));

  if (isCinema || /\b(cinema|theatre|theater|kaleidescape|dolby atmos)\b/i.test(haystack)) {
    return `This represents the complete cinema solution for the home, including image, sound, source equipment, and integration${serviceSuffix}.`;
  }

  if (
    (roles.some((role) => role === "Amplification") || /\bamplifier\b/i.test(haystack)) &&
    (roles.some((role) =>
      ["Front Speakers", "Surround Speakers", "Height Speakers", "Subwoofers"].includes(role),
    ) ||
      /\b(speaker|loudspeaker|subwoofer)\b/i.test(haystack))
  ) {
    return `This represents the complete audio solution for the home, including speakers, amplification, control, and integration${serviceSuffix}.`;
  }

  if (roles.some((role) => role === "Amplification") && /\b(multi-?room|whole[\s-]?home|distributed)\b/i.test(haystack)) {
    return `This represents the complete audio solution for the home, including speakers, amplification, control, and integration${serviceSuffix}.`;
  }

  if (roles.some((role) => role === "Automation") || /\b(savant|control4|crestron)\b/i.test(haystack)) {
    return `This represents the complete control scope for the home, bringing lighting, audio, and entertainment under one simple interface${serviceSuffix}.`;
  }

  if (roles.some((role) => role === "Networking")) {
    return `This represents the networking foundation for the home, supporting reliable connectivity for entertainment and smart devices${serviceSuffix}.`;
  }

  return `This represents the complete equipment scope for this system${serviceSuffix}.`;
}
